import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTawkTo } from '../hooks/useTawkTo';
import { FaRobot, FaShieldAlt, FaChartLine, FaGlobe } from 'react-icons/fa';
import CircuitLoader from '../components/CircuitLoader';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const LandingWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  background:
    radial-gradient(circle at 15% 20%, rgba(56, 189, 248, 0.2), transparent 55%),
    radial-gradient(circle at 85% 10%, rgba(37, 99, 235, 0.22), transparent 45%),
    linear-gradient(145deg, #020617 0%, #050b18 50%, #020617 100%);
  color: #f8fbff;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(
        90deg,
        rgba(37, 99, 235, 0.08) 1px,
        transparent 0
      ),
      linear-gradient(
        0deg,
        rgba(37, 99, 235, 0.08) 1px,
        transparent 0
      );
    background-size: 120px 120px;
    opacity: 0.35;
    pointer-events: none;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  inset: -20%;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.08) 0%, transparent 55%);
  filter: blur(80px);
  pointer-events: none;
`;

const Content = styled.main`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem;
  display: grid;
  gap: 4.5rem;

  @media (max-width: 1024px) {
    padding: 5rem 1.25rem 3rem;
    gap: 3.5rem;
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  align-items: center;
  animation: ${fadeIn} 0.6s ease-out forwards;
`;

const HeroCopy = styled.div`
  display: grid;
  gap: 1.5rem;
  justify-items: start;
  text-align: left;

  @media (max-width: 768px) {
    justify-items: center;
    text-align: center;
  }
`;

const HeroTag = styled.span`
  font-size: 0.9rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 600;
  padding: 0.35rem 1rem;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.18);
  color: #c7d2fe;
  border: 1px solid rgba(99, 102, 241, 0.4);
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4.25rem);
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.04em;
  max-width: 780px;
  text-shadow: 0 18px 40px rgba(30, 64, 175, 0.35);
`;

const HeroSubtitle = styled.p`
  max-width: 640px;
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  color: rgba(226, 232, 240, 0.86);
  line-height: 1.7;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const PrimaryCta = styled(Link)`
  padding: 0.95rem 2.75rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1.05rem;
  color: #0f172a;
  background: linear-gradient(135deg, #38bdf8 0%, #6366f1 100%);
  box-shadow: 0 15px 35px rgba(99, 102, 241, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgba(56, 189, 248, 0.35);
  }
`;

const SecondaryCta = styled(Link)`
  padding: 0.95rem 2.5rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1.05rem;
  color: #e0f2fe;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(12px);
  transition: background 0.25s ease, border-color 0.25s ease;

  &:hover {
    border-color: rgba(148, 163, 184, 0.7);
    background: rgba(30, 41, 59, 0.65);
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  align-items: center;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.75);
`;

const HeroMetaBullet = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: linear-gradient(135deg, #38bdf8, #6366f1);
    box-shadow: 0 0 10px rgba(56, 189, 248, 0.45);
  }
`;

const HeroVisual = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  padding: 2rem;
  background: radial-gradient(circle at top, rgba(56, 189, 248, 0.18), transparent 65%);
  border-radius: 32px;
  border: 1px solid rgba(37, 99, 235, 0.25);
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.7), 0 0 45px rgba(56, 189, 248, 0.25);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 18%;
    border-radius: 28px;
    border: 1px solid rgba(56, 189, 248, 0.18);
    opacity: 0.45;
    filter: blur(6px);
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .hero-loader {
    width: min(100%, 520px);
  }
`;

const HeroVisualCaption = styled.span`
  position: absolute;
  bottom: 1.4rem;
  font-size: 0.78rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.65);
  backdrop-filter: blur(4px);
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.55);
  z-index: 2;
`;

const StatsBar = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.18);
  backdrop-filter: blur(18px);
`;

const StatCard = styled.div`
  display: grid;
  gap: 0.3rem;
  justify-items: center;
  padding: 0.75rem 0.5rem;
`;

const StatValue = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #f1f5f9;
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  color: rgba(203, 213, 225, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.16em;
`;

const FeatureSection = styled.section`
  display: grid;
  gap: 2.5rem;
`;

const FeatureHeading = styled.div`
  text-align: center;
  max-width: 680px;
  margin: 0 auto;
  display: grid;
  gap: 0.75rem;
`;

const FeatureTitle = styled.h2`
  font-size: clamp(1.75rem, 3vw, 2.4rem);
  font-weight: 700;
  color: #f1f5f9;
`;

const FeatureIntro = styled.p`
  color: rgba(226, 232, 240, 0.78);
  font-size: 1rem;
  line-height: 1.7;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
`;

const FeatureCard = styled.div`
  position: relative;
  padding: 2rem 1.75rem;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.08);
  display: grid;
  gap: 1rem;
  transition: transform 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(99, 102, 241, 0.35);
  }
`;

const FeatureIcon = styled.div`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(56, 189, 248, 0.25));
  color: #38bdf8;
  font-size: 1.35rem;
  animation: ${float} 6s ease-in-out infinite;
`;

const FeatureHeadingText = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #e2e8f0;
`;

const FeatureText = styled.p`
  font-size: 0.98rem;
  color: rgba(203, 213, 225, 0.78);
  line-height: 1.65;
`;

const FooterNote = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: rgba(148, 163, 184, 0.8);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LandingPage = () => {
  useTawkTo(true); // Enable Tawk.to on Home/Landing page
  
  return (
  <LandingWrapper>
    <BackgroundGlow />
    <Content>
      <Hero>
        <HeroCopy>
          <HeroTag>AI-Driven Crypto Execution</HeroTag>
          <HeroTitle>
            Trade smarter with institutional-grade automation and transparent AI signals.
          </HeroTitle>
          <HeroSubtitle>
            Gemini AI Trading blends real-time market intelligence, risk controls, and
            seamless portfolio management to help you capture opportunities in any
            market condition.
          </HeroSubtitle>
          <CtaRow>
            <PrimaryCta to="/signup">Create free account</PrimaryCta>
            <SecondaryCta to="/signin">I already have an account</SecondaryCta>
          </CtaRow>
          <HeroMeta>
            <HeroMetaBullet>Encrypted routing layers</HeroMetaBullet>
            <HeroMetaBullet>Latency-optimized execution</HeroMetaBullet>
          </HeroMeta>
        </HeroCopy>
        <HeroVisual>
          <CircuitLoader className="hero-loader" />
          <HeroVisualCaption>Gemini Core Engine</HeroVisualCaption>
        </HeroVisual>
      </Hero>

      <StatsBar>
        <StatCard>
          <StatValue>$2.3B+</StatValue>
          <StatLabel>Total volume tracked</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>99.7%</StatValue>
          <StatLabel>Uptime this quarter</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>42</StatValue>
          <StatLabel>Supported markets</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>24/7</StatValue>
          <StatLabel>Dedicated monitoring</StatLabel>
        </StatCard>
      </StatsBar>

      <FeatureSection>
        <FeatureHeading>
          <FeatureTitle>Why teams choose Gemini AI Trading</FeatureTitle>
          <FeatureIntro>
            We provide a secure and intelligent toolkit for systematic investors,
            helping them scale strategies, automate execution, and preserve capital
            in volatile environments.
          </FeatureIntro>
        </FeatureHeading>

        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>
              <FaRobot />
            </FeatureIcon>
            <FeatureHeadingText>Adaptive AI signals</FeatureHeadingText>
            <FeatureText>
              Proprietary models digest order books, funding rates, and sentiment
              to surface the highest conviction trades every hour.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FaShieldAlt />
            </FeatureIcon>
            <FeatureHeadingText>Enterprise-grade security</FeatureHeadingText>
            <FeatureText>
              Multi-layer encryption, segregated wallets, and granular roles keep
              your capital protected and compliant by design.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FaChartLine />
            </FeatureIcon>
            <FeatureHeadingText>Performance insights</FeatureHeadingText>
            <FeatureText>
              Monitor P&amp;L, drawdowns, and execution quality in real time with
              configurable dashboards tailored to your strategy.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>
              <FaGlobe />
            </FeatureIcon>
            <FeatureHeadingText>Global coverage</FeatureHeadingText>
            <FeatureText>
              Connect to leading spot, derivatives, and DeFi venues with latency-
              optimized infrastructure across 12 regions.
            </FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </FeatureSection>

      <FooterNote>Regulated market connectivity · SOC2-ready infrastructure · 24/7 support desk</FooterNote>
    </Content>
  </LandingWrapper>
  );
};

export default LandingPage;